import type { ImageRecordMinimal, VehicleCategory } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { SyntheticEvent, useState } from 'react';
import { VehicleCategoryIcon } from './VehicleCategoryIcon';

type Size = 'thumbnail' | 'bleed' | 'header' | 'hero';

// Family images are 3:2 studio shots on a gray backdrop, not cut-outs. Each size hides the backdrop instead of framing
// it: multiply blends it into the surface, and a mask fades the photo's edges so no rectangle shows. The backdrop gray
// varies per photo (127-233 luma across a sample of 30), so no fixed brightness lifts all of them to the surface:
// each photo is measured once loaded and brightened by its own factor (see `getBackdropBrightness`).
// - thumbnail: a radial fade, no blend, so it sits on any card fill.
// - bleed: anchored right and run off the card edge, fading in from the left so text stays on a clean surface, and
//   from the top, where the photo's vignette is darkest and would read as a gray wedge at the card edge.
// - header: the worker app's car header (Claude Design "Car Header" 4b) on a page: anchored right, the car's rear past
//   the screen edge on phones, on a white spotlight with a floor shadow (see `HeaderStage`), its studio corners faded
//   in an oval. Wider screens show the whole car and fade the right edge too.
// - hero: radial fade, multiplied onto the page tint.
// The header photo's box, shared with the stage behind it. Centered without a transform: one would isolate the photo
// from the spotlight and page tint it multiplies onto.
const HEADER_BOX_CLASS =
  'pointer-events-none absolute top-1/2 -right-16 -mt-18 aspect-[3/2] w-65 max-w-none md:right-0 md:-mt-31 md:w-112';

const IMAGE_CLASS = {
  thumbnail:
    'h-10 w-15 object-cover [mask-image:radial-gradient(ellipse_58%_52%_at_50%_54%,#000_60%,transparent_100%)]',
  bleed:
    'pointer-events-none absolute top-1/2 -right-16 h-31 max-w-none -translate-y-[46%] mix-blend-multiply [mask-image:linear-gradient(to_right,transparent_0%,#000_35%),linear-gradient(to_bottom,transparent_10%,#000_26%)] [mask-composite:intersect]',
  header: `${HEADER_BOX_CLASS} mix-blend-multiply mask-radial-[65%_60%] mask-radial-at-[60%_55%] mask-radial-from-75% mask-l-from-88% mask-t-from-88% mask-b-from-90% md:mask-r-from-88%`,
  hero: 'aspect-[3/2] w-full max-w-3xl object-cover mix-blend-multiply [mask-image:radial-gradient(ellipse_50%_50%_at_center,#000_60%,transparent_100%)]'
} as const satisfies Record<Size, string>;

// The logo and icon fallbacks fill the zone the photo would, so lists mixing photos and fallbacks line up.
const FALLBACK_CLASS = {
  thumbnail: 'flex h-10 w-15 items-center justify-center',
  hero: 'flex h-32 items-center justify-center'
} as const satisfies Record<Exclude<Size, 'bleed' | 'header'>, string>;

const LOGO_CLASS = {
  thumbnail: 'max-h-7 max-w-10',
  hero: 'max-h-full max-w-full'
} as const satisfies Record<Exclude<Size, 'bleed' | 'header'>, string>;

const ICON_SIZE = { thumbnail: 24 } as const;

// Lift on top of the backdrop correction, as in the design handover. The hero keeps the raw photo.
const BASE_BRIGHTNESS = { thumbnail: 1.12, bleed: 1.1, header: 1.1, hero: undefined } as const satisfies Record<
  Size,
  number | undefined
>;

type Props = {
  car: {
    image?: ImageRecordMinimal | null;
    makeLogo?: ImageRecordMinimal | null;
    vehicleCategory?: VehicleCategory;
    make?: string;
  };
  alt: string;
  size: Size;
  className?: string;
};

/**
 * The car's model family image, else its make logo, else its vehicle category icon (not as a hero).
 * `bleed` (the car card) shows the photo, else the make's initial as a watermark, else nothing.
 * `header` (a page's car header) shows the photo, else nothing.
 * `bleed` and `header` are absolutely positioned: the parent needs `relative overflow-hidden` (`overflow-x-clip` for
 * `header`, whose photo is taller than the text), and content over it `relative`. `bleed` also needs `isolate`;
 * `header` must not have it, since its photo multiplies onto the page tint.
 */
export const CarImage = ({ car, alt, size, className }: Props) => {
  if (car.image) {
    const photo = <CarPhoto url={car.image.url} alt={alt} size={size} className={className} />;
    return size === 'header' ? (
      <>
        <HeaderStage />
        {photo}
      </>
    ) : (
      photo
    );
  }

  if (size === 'header') {
    return null;
  }

  // Car Photo Directions 2b: the make's initial as a faint watermark, oversized and cropped by the card edge like the
  // photo. Never a logo file or category icon here.
  if (size === 'bleed') {
    const initial = car.make?.trim().charAt(0).toUpperCase();
    return initial ? (
      <div
        aria-hidden
        className='pointer-events-none absolute top-1/2 -right-7.5 flex size-31 -translate-y-1/2 items-center justify-center rounded-full bg-primary/6 text-[56px] font-semibold text-primary/14'
      >
        {initial}
      </div>
    ) : null;
  }

  if (car.makeLogo) {
    return (
      <div className={cn('shrink-0', FALLBACK_CLASS[size], className)}>
        {/* Multiply drops a logo's white background into the surface behind it. */}
        <img src={car.makeLogo.url} alt={alt} className={cn('object-contain mix-blend-multiply', LOGO_CLASS[size])} />
      </div>
    );
  }

  // A scaled-up icon looks broken as a hero, so the hero shows nothing and the page closes the gap.
  if (size === 'hero') {
    return null;
  }

  return (
    <div className={cn('shrink-0 text-primary/45', FALLBACK_CLASS[size], className)}>
      <VehicleCategoryIcon vehicleCategory={car.vehicleCategory} size={ICON_SIZE[size]} />
    </div>
  );
};

/**
 * Behind the header photo, in its box: a white spotlight (Claude Design "Car Header" 3b), so the studio grey left in
 * the photo reads as the studio rather than a smudge, and a floor shadow under the wheels that grounds the car.
 */
const HeaderStage = () => (
  <div
    aria-hidden
    // Wide screens show the box's right edge, where the spotlight is still lit.
    className={cn(
      HEADER_BOX_CLASS,
      'bg-radial-[ellipse_48%_45%_at_60%_55%] from-white from-45% to-transparent md:mask-r-from-75%'
    )}
  >
    <div className='absolute top-[70%] left-[8%] h-[13%] w-[78%] bg-radial-[closest-side] from-primary/14 to-transparent' />
  </div>
);

type CarPhotoProps = { url: string; alt: string; size: Size; className?: string };

const CarPhoto = ({ url, alt, size, className }: CarPhotoProps) => {
  const baseBrightness = BASE_BRIGHTNESS[size];
  const [measured, setMeasured] = useState(() => ({ url, factor: backdropBrightnessByUrl.get(url) }));
  const factor = measured.url === url ? measured.factor : backdropBrightnessByUrl.get(url);
  // Hidden until measured, so a dark backdrop never flashes as a gray block before its correction.
  const isPending = baseBrightness !== undefined && factor === undefined;

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    if (baseBrightness !== undefined && factor === undefined) {
      setMeasured({ url, factor: getBackdropBrightness(event.currentTarget) });
    }
  };

  return (
    <img
      src={url}
      alt={alt}
      // The bucket answers CORS with `*`; without it the canvas read below throws and the photo keeps factor 1.
      crossOrigin='anonymous'
      loading={size === 'hero' ? undefined : 'lazy'}
      onLoad={handleLoad}
      style={
        baseBrightness !== undefined && factor !== undefined
          ? { filter: `brightness(${(baseBrightness * factor).toFixed(3)})` }
          : undefined
      }
      className={cn('shrink-0 transition-opacity', isPending && 'opacity-0', IMAGE_CLASS[size], className)}
    />
  );
};

// Target backdrop luma before the base lift, and the cap that keeps the car itself from washing out: the design's values.
// Backdrops darker than ~180 stay grayish; cutting cars out at upload is the real fix for those.
const BACKDROP_TARGET_LUMA = 226;
const MAX_BACKDROP_CORRECTION = 1.25;
const backdropBrightnessByUrl = new Map<string, number>();

/** Brightness factor that lifts this photo's backdrop toward white, cached per URL. 1 when the pixels are unreadable. */
const getBackdropBrightness = (image: HTMLImageElement) => {
  const cached = backdropBrightnessByUrl.get(image.src);
  if (cached !== undefined) {
    return cached;
  }
  const factor = readBackdropBrightness(image);
  backdropBrightnessByUrl.set(image.src, factor);
  return factor;
};

const readBackdropBrightness = (image: HTMLImageElement) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 24;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return 1;
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    // A band above the roof, clear of the darker vignette corners.
    const { data } = context.getImageData(10, 1, 12, 2);
    const pixelCount = data.length / 4;
    const luma =
      Array.from({ length: pixelCount }, (_, pixel) => {
        const [red = 0, green = 0, blue = 0] = data.subarray(pixel * 4, pixel * 4 + 3);
        return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
      }).reduce((sum, value) => sum + value, 0) / pixelCount;
    return luma > 0 ? Math.min(MAX_BACKDROP_CORRECTION, Math.max(1, BACKDROP_TARGET_LUMA / luma)) : 1;
  } catch {
    // A tainted canvas (no CORS) throws on read.
    return 1;
  }
};
